import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, PartyPopper, MessageCircle, Filter, Users, User,
  ArrowLeft, Send, Sparkles, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  bronze: 'bg-amber-600',
  silver: 'bg-gray-400',
  gold: 'bg-yellow-500',
  diamond: 'bg-cyan-500'
};

const REACTION_EMOJIS = ['🔥', '💯', '💪', '👏', '❤️', '🎉'];

const WinsWallPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [posts, setPosts] = useState([]);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [commentInputs, setCommentInputs] = useState({});
  const [showComments, setShowComments] = useState({});
  const [viewProfile, setViewProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const viewUserProfile = async (userId) => {
    if (userId === user.id) { navigate('/profile'); return; }
    setViewProfile(userId);
    try {
      const res = await fetch(`${API_URL}/api/participants/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setProfileData(await res.json());
    } catch (e) { console.error(e); }
  };


  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/wins-wall?filter=${filter}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setPosts(await res.json());
      } else {
        toast.error('Failed to load wins');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Connection error');
    }
    setLoading(false);
  };

  const celebratePost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/wins-wall/${postId}/celebrate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        const data = await res.json();
        setPosts(posts.map(p => 
          p.id === postId 
            ? { ...p, celebrations: data.celebrated 
                ? [...(p.celebrations || []), user.id]
                : (p.celebrations || []).filter(id => id !== user.id)
              }
            : p
        ));
      }
    } catch (error) {
      toast.error('Failed to celebrate');
    }
  };

  const reactToPost = async (postId, emoji) => {
    setShowReactionPicker(null);
    try {
      const res = await fetch(`${API_URL}/api/wins-wall/${postId}/react`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ emoji })
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(posts.map(p => p.id === postId ? { ...p, reactions: data.reactions } : p));
      }
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const addComment = async (postId) => {
    const content = commentInputs[postId];
    if (!content?.trim()) return;
    
    if (content.length > 100) {
      toast.error('Comment must be 100 characters or less');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/wins-wall/${postId}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content })
      });

      if (res.ok) {
        const data = await res.json();
        setPosts(posts.map(p => 
          p.id === postId 
            ? { ...p, comments: [...(p.comments || []), data.comment] }
            : p
        ));
        setCommentInputs({ ...commentInputs, [postId]: '' });
        toast.success('Comment added!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add comment');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const toggleComments = (postId) => {
    setShowComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors?.background || '#FEF9F3' }}>
      {/* Header */}
      <div className="p-6 rounded-b-3xl" style={{ background: colors?.header_gradient || colors?.header || '#1B2D4F', color: '#fff' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-3">
              <Trophy className="w-10 h-10" style={{ color: colors?.mode === 'dark' ? colors?.primary : '#fff' }} />
              <div>
                <h1 className="text-3xl font-bold" style={{ color: "white", fontFamily: 'var(--font-heading)' }}>Wins Wall</h1>
                <p style={{ color: "rgba(255,255,255,0.9)" }}>Celebrate achievements together! 🎉</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Post a Win Button */}
        <Button 
          onClick={() => navigate('/accomplishments')}
          className="w-full mb-4 bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-2xl py-3 font-bold text-base gap-2"
          data-testid="post-win-btn"
        >
          <Trophy className="w-5 h-5" /> Post a Win
        </Button>
        
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="w-full bg-white rounded-2xl p-1 shadow-sm">
            <TabsTrigger value="all" className="flex-1 rounded-xl" data-testid="filter-all">
              <Sparkles className="w-4 h-4 mr-2" /> Everyone
            </TabsTrigger>
            <TabsTrigger value="group" className="flex-1 rounded-xl" data-testid="filter-group">
              <Users className="w-4 h-4 mr-2" /> My Group
            </TabsTrigger>
            <TabsTrigger value="mine" className="flex-1 rounded-xl" data-testid="filter-mine">
              <User className="w-4 h-4 mr-2" /> My Wins
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Posts */}
      <div className="max-w-4xl mx-auto px-4 space-y-4">
        {posts.length === 0 ? (
          <Card className="p-8 text-center rounded-3xl">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No wins yet!</h2>
            <p className="text-muted-foreground">
              Complete goals and add accomplishments to see them here.
            </p>
            <Button onClick={() => navigate('/goals')} className="mt-4 rounded-full">
              Set Some Goals
            </Button>
          </Card>
        ) : (
          <AnimatePresence>
            {posts.map((post, index) => {
              const isCelebrated = post.celebrations?.includes(user.id);
              const celebrationCount = post.celebrations?.length || 0;
              const commentCount = post.comments?.length || 0;

              return (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="p-5 rounded-3xl bg-white shadow-sm hover:shadow-md transition-all">
                    {/* Post Header */}
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12 ring-2 ring-yellow-100">
                        {post.user_profile_picture ? (
                          <AvatarImage src={post.user_profile_picture} alt={post.user_name} />
                        ) : null}
                        <AvatarFallback className="bg-gradient-to-br from-yellow-100 to-orange-100 text-2xl">
                          {post.user_avatar || '😊'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold cursor-pointer hover:text-orange-600 hover:underline transition-colors" onClick={(e) => { e.stopPropagation(); viewUserProfile(post.user_id); }} data-testid={`user-name-${post.id}`}>{post.user_name}</span>
                          {/* User Badges */}
                          {post.user_badges && post.user_badges.length > 0 && (
                            <div className="flex items-center gap-1">
                              {post.user_badges.map((badge, idx) => (
                                <span 
                                  key={idx}
                                  className={`w-5 h-5 rounded-full ${TIER_COLORS[badge.tier]} flex items-center justify-center text-xs`}
                                  title={badge.name}
                                >
                                  {badge.icon}
                                </span>
                              ))}
                            </div>
                          )}
                          <Badge className={
                            post.post_type === 'goal_completed' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-purple-100 text-purple-800'
                          }>
                            {post.post_type === 'goal_completed' ? '🎯 Goal' : '🏆 Win'}
                          </Badge>
                          {post.goal_type && (
                            <Badge variant="outline" className="text-xs">
                              {post.goal_type === 'long_term' ? 'Long-term' : 'Summer'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(post.created_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="mt-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-4">
                      <h3 className="font-bold text-lg">{post.title}</h3>
                      <p className="text-muted-foreground mt-1">{post.description}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-4 flex-wrap">
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => celebratePost(post.id)}
                        data-testid={`celebrate-btn-${post.id}`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                          isCelebrated 
                            ? 'bg-yellow-100 text-yellow-700' 
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        <PartyPopper className={`w-5 h-5 ${isCelebrated ? 'text-yellow-500' : ''}`} />
                        <span className="font-semibold">{celebrationCount}</span>
                        <span className="text-sm">Celebrate!</span>
                      </motion.button>

                      {/* Emoji Reaction Button */}
                      <div className="relative">
                        <button
                          onClick={() => setShowReactionPicker(showReactionPicker === post.id ? null : post.id)}
                          className="flex items-center gap-1 px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all text-sm"
                          data-testid={`react-btn-${post.id}`}
                        >
                          <span>+</span>
                          <span className="text-base">😊</span>
                        </button>
                        {showReactionPicker === post.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 5 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            className="absolute bottom-full left-0 mb-2 flex gap-1 bg-white rounded-2xl shadow-lg border p-2 z-10"
                          >
                            {REACTION_EMOJIS.map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => reactToPost(post.id, emoji)}
                                className="text-xl hover:scale-125 transition-transform p-1 rounded-lg hover:bg-gray-100"
                                data-testid={`emoji-${emoji}-${post.id}`}
                              >
                                {emoji}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </div>

                      <button
                        onClick={() => toggleComments(post.id)}
                        data-testid={`comments-btn-${post.id}`}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span className="font-semibold">{commentCount}</span>
                        <span className="text-sm">Comments</span>
                      </button>
                    </div>

                    {/* Reaction Summary */}
                    {post.reactions && Object.keys(post.reactions).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(post.reactions).map(([emoji, users]) => (
                          <button
                            key={emoji}
                            onClick={() => reactToPost(post.id, emoji)}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-sm transition-all ${
                              users.includes(user.id) 
                                ? 'bg-blue-100 border border-blue-300' 
                                : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            <span>{emoji}</span>
                            <span className="font-medium text-xs">{users.length}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Comments Section */}
                    <AnimatePresence>
                      {showComments[post.id] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 border-t pt-4"
                        >
                          {/* Existing Comments */}
                          {post.comments?.length > 0 && (
                            <div className="space-y-3 mb-4">
                              {post.comments.map((comment) => (
                                <div key={comment.id} className="flex items-start gap-3 bg-gray-50 rounded-xl p-3">
                                  <span className="text-xl">{comment.user_avatar}</span>
                                  <div>
                                    <span className="font-semibold text-sm">{comment.user_name}</span>
                                    <p className="text-sm">{comment.content}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add Comment */}
                          {commentCount < 10 ? (
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add a supportive comment... (100 chars max)"
                                value={commentInputs[post.id] || ''}
                                onChange={(e) => setCommentInputs({
                                  ...commentInputs,
                                  [post.id]: e.target.value.slice(0, 100)
                                })}
                                onKeyPress={(e) => e.key === 'Enter' && addComment(post.id)}
                                className="rounded-xl"
                                maxLength={100}
                              />
                              <Button 
                                onClick={() => addComment(post.id)}
                                size="icon"
                                className="rounded-xl"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground text-center">
                              Maximum comments reached
                            </p>
                          )}
                          
                          {commentInputs[post.id] && (
                            <p className="text-xs text-muted-foreground mt-1 text-right">
                              {commentInputs[post.id].length}/100
                            </p>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {/* Profile Popup */}
      {viewProfile && profileData && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => { setViewProfile(null); setProfileData(null); }}>
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="bg-white rounded-t-3xl w-full max-w-lg max-h-[70vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="w-10 h-1.5 bg-gray-200 rounded-full mx-auto mb-4" />
              <div className="flex items-center gap-4 mb-4">
                <Avatar className="w-14 h-14 ring-2 ring-orange-100">
                  <AvatarFallback className="bg-orange-50 text-2xl">{profileData.avatar_emoji || profileData.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-bold text-lg">{profileData.name}</h3>
                  {profileData.work_site && <p className="text-sm text-muted-foreground">{profileData.work_site}</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-orange-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-orange-600">{profileData.total_points}</p>
                  <p className="text-[10px] text-gray-500">Points</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-blue-600">#{profileData.rank}</p>
                  <p className="text-[10px] text-gray-500">Rank</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-green-600">{profileData.goals_completed_count}</p>
                  <p className="text-[10px] text-gray-500">Goals Done</p>
                </div>
              </div>
              {profileData.career_industries?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-bold text-gray-500 mb-1">Career Interests</p>
                  <div className="flex flex-wrap gap-1">{profileData.career_industries.map((c, i) => (
                    <Badge key={i} className="bg-purple-50 text-purple-700 text-xs">{c}</Badge>
                  ))}</div>
                </div>
              )}
              {profileData.badges?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 mb-1">Badges ({profileData.badges_count})</p>
                  <div className="flex flex-wrap gap-1">{profileData.badges.map((b, i) => (
                    <span key={i} className={`w-7 h-7 rounded-full ${TIER_COLORS[b.tier] || 'bg-gray-200'} flex items-center justify-center text-xs`} title={b.name}>{b.icon}</span>
                  ))}</div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <Navigation role="youth" />
    </div>
  );
};

export default WinsWallPage;
